import React, { useEffect } from 'react';
import classNames from 'classnames';

import { useLocation } from 'react-router-dom';
import { useUpdateEffect } from '~shared/hooks/useUpdateEffect';

import {
    useSsDispatch as useDispatch,
    useSsSelector as useSelector,
} from '~SpectateStore';

import {
    useContainerSize,
} from '~SpectateStore/hooks/clientStateHooks';
import {
    useIsUserLoggedIn,
} from '~SpectateStore/hooks/userHooks';
import {
    getTotalOpenBets,
} from './thunks/getTotalOpenBets';

import {
    setFullScreenOff,
    updateOpenBets,
    pinTab,
    unpinTab,
} from '~Betslip/slices/uiSlice';

import {
    selectUiMode,
    selectIsFullScreen,
    selectDisplayFullslip,
    selectShowModal,
    selectPinnedTab,
} from '~Betslip/selectors/uiSelectors';

import {
    KeyboardProvider,
} from '~Betslip/store/keyboardContext';
import KeyboardPortal from './uiModes/Full/Components/Keyboard/KeyboardPortal';

import Baby from './uiModes/Baby';
import Full from './uiModes/Full';
import Modal from './uiModes/shared/Modal';

import { initialiseWsHandlers } from './interface/wsHandlers';

import { useModal } from '~shared/components/modal-overlay/ModalContext';

import {
    selectSelectionDetails,
    selectCountSelectionDetails,
} from '~Betslip/selectors/selectionDetailsSelectors';

import {
    selectRecommendations,
    selectCountRecommendations,
} from '~Betslip/selectors/recommendationsSelectors';

import {
    wsSubscribe,
    wsUnsubscribe,
} from '~Betslip/interface/wsHandlers';

import {
    setSiteScrollDisabled,
} from '~spa/Utils/WrapperIntegration/Ucf';

import {
    selectShowContingenciesNotification,
} from '~Betslip/selectors/contingenciesSelectors';

import { MODAL_TYPES } from '~shared/components/modal-overlay/constants';

import './interface/externalClickHandler';
import './betslip.scss';

const BetslipContainer = () => {

    const dispatch = useDispatch();
    const uiMode = useSelector(selectUiMode);
    const displayFullslip = useSelector(selectDisplayFullslip);
    const isFullScreen = useSelector(selectIsFullScreen);
    const showModal = useSelector(selectShowModal);
    const {
        pushModal,
        removeModalsWithId,
    } = useModal();
    const isLoggedIn = useIsUserLoggedIn();
    const containerSize = useContainerSize();
    const selectionDetails = useSelector(selectSelectionDetails);
    const selectionsDetailsCount = useSelector(selectCountSelectionDetails);
    const recommendations = useSelector(selectRecommendations);
    const recommendationsCount = useSelector(selectCountRecommendations);

    const showContingenciesNotification = useSelector(selectShowContingenciesNotification);
    const pinnedTab = useSelector(selectPinnedTab);
    const { pathname: currentUrlPath } = useLocation();

    useEffect(() => {
        initialiseWsHandlers();
    }, []);

    useUpdateEffect(() => {
        dispatch(setFullScreenOff());
        setSiteScrollDisabled(false);
    }, [containerSize, currentUrlPath]);

    // subscribe/unsubscribe to websocket updates when the selections count changes
    useEffect(() => {
        wsSubscribe(selectionDetails);

        return () => {
            wsUnsubscribe(selectionDetails);
        };
    }, [selectionsDetailsCount]);

    // subscribe/unsubscribe to websocket updates when the selections count changes
    useEffect(() => {
        wsSubscribe(recommendations);

        return () => {
            wsUnsubscribe(recommendations);
        };
    }, [recommendationsCount]);

    useEffect(() => {
        if (showModal) {
            pushModal(<Modal />, MODAL_TYPES.BETSLIP);
        } else {
            removeModalsWithId(MODAL_TYPES.BETSLIP);
        }
    }, [showModal]);

    useEffect(() => {
        if (isLoggedIn) {
            dispatch(getTotalOpenBets());
        } else {
            dispatch(updateOpenBets(0));
        }
    }, [isLoggedIn]);

    useEffect(() => {
        const singleTab = 'singles';

        if (isFullScreen && showContingenciesNotification && !pinnedTab) {
            dispatch(pinTab(singleTab));
        }

        if (!isFullScreen && pinnedTab === singleTab) {
            dispatch(unpinTab());
        }
    }, [isFullScreen, uiMode]);

    const betslipClass = classNames(
        'betslip',
        isFullScreen ? 'betslip--fullscreen' : `betslip--${uiMode}`,
    );

    return (
        <KeyboardProvider>
            <div className={betslipClass}>
                {
                    displayFullslip
                        ? <Full />
                        : <Baby />
                }
                <KeyboardPortal />
            </div>
        </KeyboardProvider>
    );
};

export default BetslipContainer;
